import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, CalendarDays, ChevronDown } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";

const experienceList = [
  {
    role: "Lead Software Development Engineer in Test",
    company: "S&P Global",
    duration: "Oct 2024 – Present",
    current: true,
    details: [
      "Developed an AI-driven Selenium UI automation workflow that analyzes existing framework patterns and reusable components, uses Playwright MCP to understand live UI and locator/XPath logic, and automatically plans, generates, executes, and debugs scripts from test steps, reducing manual automation effort.",
      "Architected and implemented an AI-driven Automation Agent for Karate API testing that performs impact analysis, duplicate scenario detection, reusable component discovery, implementation planning, automated script generation, and RCA, reducing manual automation effort by up to 90%.",
      "Developed automation framework using Selenium, Java, OOPs, TestNG and POM which reduced manual efforts by 70% and improved framework performance by optimizing browser launch implementation, reducing setup time by 10%.",
      "Implemented AI-based Self-Healing Locator logic in the existing automation framework, reducing test failures by 60% and locator creation effort by 80%.",
      "Worked with Playwright MCP for web automation and did Playwright MCP POC leveraging AI/Copilot-based natural language prompts to enable zero-code automated test execution.",
      "Analyzed client requirements, estimated testing efforts, identified risks, participated in sprint planning and defect triage discussions, and supported release sign-off activities.",
      "Reviewed automation code, mentored team members, conducted KT sessions, established framework best practices, and supported debugging and delivery of automation solutions.",
    ],
  },
  {
    role: "Software Development Engineer in Test",
    company: "Tata Consultancy Services",
    duration: "Oct 2022 – Oct 2024",
    current: false,
    details: [
      "Developed and maintained automation scripts using Selenium, Java, TestNG and Page Object Model (POM), reducing regression testing time by 30% through cross-browser testing, dynamic locators, waits implementation and parallel execution.",
      "Designed Master Test Plan (MTP) based on client requirements and created positive and negative test cases covering automation, performance and manual testing for web applications.",
      "Performed performance testing using JMeter by creating scripts from scratch, executing load, stress, and endurance tests, analyzing test results, and sharing performance insights and reports with development and DevOps teams.",
      "Performed REST API testing using Postman to validate GET, POST, PUT and DELETE endpoints.",
      "Conducted multiple testing types including Functional, UI, Adhoc, Black Box, Regression, Integration, Smoke, Sanity, Stress, Load and Endurance testing.",
    ],
  },
  {
    role: "Tron SME",
    company: "Amazon",
    duration: "May 2019 – Sep 2022",
    current: false,
    details: [
      "Supported Amazon Robotics Fulfilment Centres by verifying stowing activities through the Writing Tool and providing machine learning validation.",
      "Defined standard operating procedures and performance benchmarks, led a 10-member team as SME.",
      "Tracked and analysed team performance in Excel to help with better planning and decisions.",
      "Led daily team meetings and reviewed weekly progress to find and fix issues faster.",
      "Consistently achieved over 98% accuracy by focusing on quality and attention to detail.",
    ],
  },
];

/* ─── Timeline Dot ─── */
const TimelineDot = ({ isCurrent }) => (
  <div className="relative flex items-center justify-center flex-shrink-0">
    {isCurrent ? (
      <>
        <span className="absolute w-5 h-5 rounded-full bg-cyan-400/50 dark:bg-cyan-400/30 animate-ping" />
        <span className="relative w-3 h-3 rounded-full bg-cyan-500 dark:bg-cyan-400 ring-[3px] ring-cyan-100 dark:ring-cyan-900/50 shadow-lg shadow-cyan-500/30" />
      </>
    ) : (
      <span className="w-3 h-3 rounded-full bg-blue-400 dark:bg-blue-500 ring-[3px] ring-blue-100 dark:ring-blue-900/40 shadow-sm" />
    )}
  </div>
);

/* ─── Experience Card ─── */
const ExperienceCard = ({ exp, index, isOpen, onToggle }) => (
  <motion.div
    layout
    transition={{ duration: 0.3, ease: "easeInOut" }}
    className={`rounded-xl border overflow-hidden transition-all duration-300
      ${isOpen
        ? "border-blue-400 dark:border-blue-500 shadow-lg shadow-blue-100/60 dark:shadow-blue-900/30"
        : "border-gray-200 dark:border-gray-700 shadow-sm hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md"
      }
      bg-white dark:bg-gray-800
    `}
  >
    {/* Header */}
    <div
      className="flex items-start justify-between gap-4 px-4 sm:px-5 py-4 cursor-pointer"
      onClick={() => onToggle(index)}
    >
      {/* Left content */}
      <div className="flex flex-col gap-1 min-w-0">
        {/* Company row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-blue-600 dark:text-blue-400 flex-shrink-0">
            <Building2 size={16} />
          </span>
          <span className="text-blue-700 dark:text-blue-400 font-semibold text-sm">
            {exp.company}
          </span>
        </div>

        {/* Role */}
        <p className="font-bold text-gray-800 dark:text-white text-sm sm:text-base leading-snug">
          {exp.role}
        </p>

        {/* Duration */}
        <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
          <CalendarDays size={13} />
          <span>{exp.duration}</span>
        </div>
      </div>

      {/* Chevron — pinned top-right */}
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="flex-shrink-0 mt-0.5 text-blue-500 dark:text-blue-400"
      >
        <ChevronDown size={20} />
      </motion.div>
    </div>

    {/* Expandable content — NO internal scroll */}
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          {/* Subtle divider */}
          <div className="mx-4 border-t border-gray-100 dark:border-gray-700" />

          <div className="px-4 sm:px-5 pb-5 pt-3">
            <ul className="space-y-2.5">
              {exp.details.map((point, idx) => (
                <motion.li
                  key={`${index}-${idx}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                  className="flex items-start gap-3 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed"
                >
                  {/* Small dot bullet */}
                  <span className="flex-shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                  <span>{point}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

/* ─── Main Experience Section ─── */
const Experience = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const toggleExperience = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section
      id="experience"
      className="pt-4 pb-10 px-4 sm:px-6 md:px-16 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto max-w-screen-xl">
        <SectionHeading
          title="Experience"
          subtitle="Where I've made an impact"
          gradient="blue"
        />

        {/* Timeline layout */}
        <div className="flex flex-col">
          {experienceList.map((exp, index) => {
            const isLast = index === experienceList.length - 1;
            return (
              <div key={index} className="flex gap-3 md:gap-5">
                {/* Timeline rail: hidden on mobile, visible on md+ */}
                <div className="hidden md:flex flex-col items-center pt-5 self-stretch">
                  <TimelineDot isCurrent={exp.current} />
                  {!isLast && (
                    <div className="w-px flex-1 border-l-2 border-dashed border-gray-300 dark:border-gray-600" />
                  )}
                </div>

                {/* Card */}
                <div className={`flex-1 min-w-0 ${!isLast ? "pb-6" : ""}`}>
                  <ExperienceCard
                    exp={exp}
                    index={index}
                    isOpen={expandedIndex === index}
                    onToggle={toggleExperience}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience;